<template>
  <div class="stop-page">
    <!-- 左侧站点列表 -->
    <div class="stop-list-panel">
      <div class="query-item">
        <div class="search-l">
          <div>
            <a-input class="inp ml24" placeholder="请输入站点名称" v-model:value="query.name"></a-input>
          </div>
          <a-button class="ml30" @click="search" type="primary" ghost>查询</a-button>
          <a-button class="ml24" @click="reset" type="primary" ghost>重置</a-button>
          <div></div>
        </div>
        <div class="search-r">
          <a-button @click="add" type="primary">新增</a-button>
        </div>
      </div>

      <a-table
        :columns="columns"
        :row-key="(record) => record.id"
        :data-source="dataSource"
        size="middle"
        :pagination="pagination"
        :scroll="{ y: 'auto' }"
        v-autoHeight="{ extraHeight: 120, trHeight: 47 }"
        :row-class-name="(record) => record.id === selectedStopId ? 'ant-table-row-selected' : ''"
        :customRow="customRow"
      >
        <template #bodyCell="{ column, record, index }">
          <template v-if="column.key === 'ranking'">
            {{ (pagination.current - 1) * pagination.pageSize + index + 1 }}
          </template>
          <template v-else-if="column.key === 'action'">
            <span>
              <a @click.stop="edit(record)">编辑</a>
              <a class="ml10" style="color: #ff4d4f" @click.stop="remove(record)">删除</a>
            </span>
          </template>
        </template>
      </a-table>
    </div>

    <!-- 右侧地图 -->
    <div class="map-panel">
      <div id="stop-map-container" class="map-container"></div>
      <div v-if="mapLoading" class="loading-overlay">
        <a-spin size="large" />
        <span class="loading-text">地图加载中...</span>
      </div>
      <!-- 站点详情信息浮层 -->
      <div v-if="selectedStop" class="stop-info-overlay">
        <div class="stop-info-header">
          <span class="stop-info-title">{{ selectedStop.name }}</span>
          <a class="stop-info-close" @click="clearSelection">×</a>
        </div>
        <div class="stop-info-content">
          <div class="stop-info-section">
            <div class="section-label">基本信息</div>
            <div class="stop-info-row">
              <span class="stop-info-label">城市</span>
              <span class="stop-info-value">{{ selectedStop.cityName || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">厂商</span>
              <span class="stop-info-value">{{ selectedStop.providerName || selectedStop.provinceName || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">厂商站点编码</span>
              <span class="stop-info-value">{{ selectedStop.providerStopId || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">站点名称</span>
              <span class="stop-info-value">{{ selectedStop.name || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">站点位置</span>
              <span class="stop-info-value">{{ selectedStop.address || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">经纬度</span>
              <span class="stop-info-value">{{ selectedStop.coordinates || '-' }}</span>
            </div>
            <div class="stop-info-row">
              <span class="stop-info-label">经纬度（gcj02）</span>
              <span class="stop-info-value">{{ selectedStop.gcj02Coordinates || '-' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <a-modal v-model:open="modalVisible" title="站点信息" width="720px">
      <a-form :model="formData" :label-col="{ span: 5 }" :wrapper-col="{ span: 16 }">
        <a-form-item label="城市" required>
          <a-select v-model:value="formData.cityId" placeholder="请选择城市" style="width: 100%">
            <a-select-option v-for="city in cityList" :key="city.id" :value="city.id">
              {{ city.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="厂商" required>
          <a-select v-model:value="formData.providerCode" placeholder="请选择厂商" style="width: 100%">
            <a-select-option v-for="provider in providerList" :key="provider.code" :value="provider.code">
              {{ provider.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="厂商站点ID" required>
          <a-input v-model:value="formData.providerStopId" placeholder="请输入厂商站点ID" />
        </a-form-item>
        <a-form-item label="站点名称" required>
          <a-input v-model:value="formData.name" placeholder="请输入站点名称" />
        </a-form-item>
        <a-form-item label="站点位置" required>
          <a-input v-model:value="formData.address" placeholder="请输入位置" />
        </a-form-item>
        <a-form-item label="经纬度" required>
          <a-input v-model:value="formData.coordinates" placeholder="格式：经度,纬度" />
        </a-form-item>
        <a-form-item label="经纬度（gcj02）" required>
          <a-input v-model:value="formData.gcj02Coordinates" placeholder="格式：经度,纬度" />
        </a-form-item>
      </a-form>
      <template #footer>
        <a-button @click="modalVisible = false">取消</a-button>
        <a-button type="primary" @click="save">确定</a-button>
      </template>
    </a-modal>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, onBeforeUnmount, nextTick } from "vue";
import { message, Modal } from 'ant-design-vue'
import AMapLoader from '@amap/amap-jsapi-loader'
import { addStop, deleteStop, getStopPageList, getStopList, getStop, updateStop } from '@/api/roads/stop.js'
import { getCityList } from '@/api/roads/city.js'
import { getProviderList } from '@/api/settings/provider.js'

const columns = [
  { title: "序号", key: "ranking", width: 50 },
  { title: "厂商", dataIndex: "provinceName", key: "provinceName", width: 100 },
  { title: "厂商站点编码", dataIndex: "providerStopId", key: "providerStopId", width: 120 },
  { title: "站点名称", dataIndex: "name", key: "name", width: 220 },
  { title: "操作", key: "action", dataIndex: "action", width: 100 },
];

let dataSource = ref([]);
const allStops = ref([]);
let query = reactive({ name: "" });

const pagination = reactive({
  total: 0,
  size: "middle",
  defaultPageSize: 20,
  current: 1,
  pageSize: 20,
  showSizeChanger: true,
  showQuickJumper: false,
  pageSizeOptions: ["10", "20", "30"],
  showTotal: (total) => `共有 ${total} 条数据`,
  onShowSizeChange: (current, pageSize) => (pagination.pageSize = pageSize),
  onChange: pageChange,
  showSizeChange: pageSizeChange,
});

// 地图相关
const mapLoading = ref(false);
let map = null;
let AMapInstance = null;
let allMarkers = [];
let allOverlays = [];
const selectedStopId = ref(null);
const selectedStop = ref(null);

const modalVisible = ref(false);
const cityList = ref([]);
const providerList = ref([]);
const formData = reactive({
  id: null,
  cityId: null,
  providerCode: "",
  name: "",
  providerStopId: "",
  address: "",
  coordinates: "",
  gcj02Coordinates: ""
});

function pageChange(page, pageSize) {
  pagination.current = page;
  getData();
}

function pageSizeChange(current, size) {
  pagination.pageSize = size;
  getData();
}

const search = () => {
  pagination.current = 1;
  getData();
};

const reset = () => {
  query.name = "";
  pagination.current = 1;
  getData();
};

const customRow = (record) => ({
  onClick: () => {
    handleSelectStop(record);
  },
  style: { cursor: 'pointer' },
});

// 解析经纬度字符串为 [lng, lat]
const parseCoordinates = (coordStr) => {
  if (!coordStr || typeof coordStr !== 'string') return null;
  const parts = coordStr.split(',').map((s) => parseFloat(s.trim()));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
  return parts;
};

const getData = async () => {
  const data = {
    pageIndex: pagination.current,
    pageSize: pagination.pageSize,
    conditionMap: { ...query },
  };
  try {
    const res = await getStopPageList(data);
    if (res.code === 200) {
      dataSource.value = res.data.records || res.data.list || [];
      pagination.total = res.data.total || 0;
    }
  } catch (error) {
    console.error('获取站点列表失败:', error);
  }
};

// 获取所有站点（用于地图绘制，不受分页影响）
const loadAllStops = async () => {
  try {
    const res = await getStopList();
    if (res.code === 200) {
      allStops.value = res.data || [];
      drawAllStops();
    }
  } catch (error) {
    console.error('获取所有站点失败:', error);
  }
};

// 加载高德地图 SDK
const loadAMapSDK = async () => {
  const opts = {
    key: '6588d9e5b6ae0751c799cb4c7b3e30be',
    version: '2.0',
    plugins: ['AMap.ToolBar', 'AMap.Scale'],
  };
  try {
    return await AMapLoader.load(opts);
  } catch (error) {
    const errMsg = (error && error.message) || error || '';
    if (!errMsg && typeof AMapLoader.reset === 'function') {
      AMapLoader.reset();
      return await AMapLoader.load(opts);
    }
    throw error;
  }
};

// 初始化地图
const initMap = async () => {
  mapLoading.value = true;
  try {
    const mapContainer = document.getElementById('stop-map-container');
    if (mapContainer && (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0)) {
      mapContainer.style.minHeight = '400px';
      await nextTick();
    }

    const AMap = await loadAMapSDK().catch((error) => {
      throw new Error(`高德地图API加载失败: ${error.message || error}`);
    });

    AMapInstance = AMap;
    map = new AMap.Map('stop-map-container', {
      zoom: 12,
      center: [103.8349, 36.0680],
      mapStyle: 'amap://styles/whitesmoke',
      viewMode: '2D',
      zooms: [2, 20],
      dragEnable: true,
      animateEnable: true,
      showZoomControl: false,
    });

    map.addControl(new AMap.ToolBar({ position: 'RB', offset: [10, 60], liteStyle: true }));

    const handleResize = () => {
      if (map) {
        try {
          map.resize();
        } catch (error) {
          console.warn('地图尺寸调整失败:', error);
        }
      }
    };
    window.addEventListener('resize', handleResize);

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });
  } catch (error) {
    console.error('地图初始化失败:', error);
    message.error(error.message || '地图初始化失败，请刷新页面重试');
  } finally {
    mapLoading.value = false;
  }
};

// 清理所有地图覆盖物
const clearAllOverlays = () => {
  if (!map) return;
  allMarkers.forEach((m) => {
    try { map.remove(m); } catch (e) { /* ignore */ }
  });
  allOverlays.forEach((o) => {
    try { map.remove(o); } catch (e) { /* ignore */ }
  });
  allMarkers = [];
  allOverlays = [];
};

// 绘制所有站点
const drawAllStops = () => {
  if (!map || !AMapInstance) return;
  clearAllOverlays();

  const validPoints = [];
  allStops.value.forEach((stop) => {
    const coord = parseCoordinates(stop.coordinates || stop.gcj02Coordinates);
    if (!coord) return;
    validPoints.push({ coord, stop });

    const isSelected = stop.id === selectedStopId.value;
    const marker = new AMapInstance.CircleMarker({
      center: coord,
      radius: 5,
      fillColor: isSelected ? '#ff4d4f' : '#1890ff',
      fillOpacity: 0.8,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      strokeOpacity: 1,
      cursor: 'pointer',
      zIndex: 100,
    });
    marker.setMap(map);
    marker.on('click', () => {
      handleSelectStop(stop);
    });
    allMarkers.push(marker);
  });

  // 适配所有站点的视野
  if (validPoints.length > 0) {
    const lngs = validPoints.map((p) => p.coord[0]);
    const lats = validPoints.map((p) => p.coord[1]);
    const southWest = [Math.min(...lngs), Math.min(...lats)];
    const northEast = [Math.max(...lngs), Math.max(...lats)];
    map.setBounds(new AMapInstance.Bounds(southWest, northEast), false, [50, 50, 50, 50]);
  }
};

// 聚焦选中站点
const focusStop = (stop) => {
  if (!map || !AMapInstance) return;
  const coord = parseCoordinates(stop.coordinates || stop.gcj02Coordinates);
  if (!coord) return;
  map.setZoomAndCenter(15, coord);
};

// 选中站点查看详情
const handleSelectStop = async (record) => {
  selectedStopId.value = record.id;
  drawAllStops();
  focusStop(record);
  try {
    const res = await getStop(record.id);
    if (res.code === 200) {
      selectedStop.value = res.data || {};
    } else {
      // 接口失败则使用列表数据兜底
      selectedStop.value = { ...record };
    }
  } catch (error) {
    console.error('获取站点详情失败:', error);
    selectedStop.value = { ...record };
  }
};

// 取消选中
const clearSelection = () => {
  selectedStopId.value = null;
  selectedStop.value = null;
  drawAllStops();
};

const add = () => {
  formData.id = null;
  formData.cityId = null;
  formData.providerCode = "";
  formData.name = "";
  formData.providerStopId = "";
  formData.address = "";
  formData.coordinates = "";
  formData.gcj02Coordinates = "";
  modalVisible.value = true;
};

const edit = (record) => {
  formData.id = record.id;
  formData.cityId = record.cityId || null;
  formData.providerCode = record.providerCode || "";
  formData.name = record.name || "";
  formData.providerStopId = record.providerStopId || "";
  formData.address = record.address || "";
  formData.coordinates = record.coordinates || "";
  formData.gcj02Coordinates = record.gcj02Coordinates || "";
  modalVisible.value = true;
};

const remove = (record) => {
  Modal.confirm({
    title: '确认删除',
    content: `确定要删除站点「${record.name}」吗？`,
    okText: '确定',
    cancelText: '取消',
    onOk: () => {
      deleteStop(record.id).then((res) => {
        if (res.code === 200) {
          message.success('删除成功');
          if (selectedStopId.value === record.id) {
            selectedStopId.value = null;
            selectedStop.value = null;
          }
          getData();
          loadAllStops();
        } else {
          message.error(res.message || '删除失败');
        }
      }).catch(() => {
        message.error('删除失败');
      });
    }
  });
};

const save = () => {
  if (!formData.cityId) {
    message.error('请选择城市');
    return;
  }
  if (!formData.providerCode) {
    message.error('请选择厂商');
    return;
  }
  if (!formData.name) {
    message.error('请输入站点名称');
    return;
  }
  if (!formData.providerStopId) {
    message.error('请输入厂商站点ID');
    return;
  }
  if (!formData.address) {
    message.error('请输入位置');
    return;
  }
  if (!formData.coordinates) {
    message.error('请输入经纬度');
    return;
  }
  if (!formData.gcj02Coordinates) {
    message.error('请输入经纬度（gcj02）');
    return;
  }

  const data = {
    id: formData.id,
    cityId: formData.cityId,
    providerCode: formData.providerCode,
    name: formData.name,
    providerStationId: formData.providerStopId,
    address: formData.address,
    coordinates: formData.coordinates,
    gcj02Coordinates: formData.gcj02Coordinates
  };

  const api = formData.id ? updateStop : addStop;
  api(data).then((res) => {
    if (res.code === 200) {
      message.success(formData.id ? '修改成功' : '新增成功');
      modalVisible.value = false;
      getData();
      loadAllStops();
    } else {
      message.error(res.message || (formData.id ? '修改失败' : '新增失败'));
    }
  }).catch(() => {
    message.error(formData.id ? '修改失败' : '新增失败');
  });
};

const loadCityList = async () => {
  try {
    const res = await getCityList();
    if (res.code === 200 && res.data) {
      cityList.value = res.data;
    }
  } catch (error) {
    console.error('获取城市列表失败:', error);
  }
};

const loadProviderList = async () => {
  try {
    const res = await getProviderList({});
    if (res.code === 200 && res.data) {
      providerList.value = res.data;
    }
  } catch (error) {
    console.error('获取厂商列表失败:', error);
  }
};

onMounted(async () => {
  await nextTick();
  await initMap();
  await getData();
  loadAllStops();
  loadCityList();
  loadProviderList();
});

onBeforeUnmount(() => {
  if (map) {
    map.destroy();
    map = null;
  }
});
</script>

<style lang="less" scoped>
.stop-page {
  display: flex;
  gap: 16px;
  padding: 16px;
  height: calc(100vh - 70px);
  background-color: #ffffff;
}

.stop-list-panel {
  width: 600px;
  flex-shrink: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inp {
  width: 13.5vw;
}

.query-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  .search-l {
    display: flex;
  }

  .search-r {
    display: flex;
  }
}

.map-panel {
  flex: 1;
  position: relative;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-width: 0;

  .map-container {
    width: 100%;
    height: 100%;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    z-index: 10;

    .loading-text {
      font-size: 14px;
      color: #666;
    }
  }

  .stop-info-overlay {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 360px;
    max-height: calc(100% - 24px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    z-index: 10;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .stop-info-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      background: linear-gradient(135deg, #1890ff, #096dd9);

      .stop-info-title {
        font-size: 15px;
        font-weight: 600;
        color: #fff;
      }

      .stop-info-close {
        font-size: 16px;
        color: rgba(255, 255, 255, 0.8);
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #fff;
        }
      }
    }

    .stop-info-content {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0;
    }

    .stop-info-section {
      padding: 8px 16px;

      .section-label {
        font-size: 12px;
        font-weight: 600;
        color: #8c8c8c;
        margin-bottom: 6px;
        padding-left: 6px;
        border-left: 3px solid #1890ff;
        line-height: 1;
      }
    }

    .stop-info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 0;
      line-height: 1.5;

      .stop-info-label {
        font-size: 13px;
        color: #8c8c8c;
        flex-shrink: 0;
        white-space: nowrap;
      }

      .stop-info-value {
        font-size: 13px;
        color: #262626;
        text-align: right;
        margin-left: 12px;
        word-break: break-all;
      }
    }
  }
}

/deep/.ant-input::placeholder {
  font-size: 14px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: #68767e;
}

/deep/.ant-select-selector {
  background-color: #f2f4f7 !important;
  color: #68767e !important;
}

/deep/ .ant-table-row-selected td {
  background-color: #e6f7ff !important;
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th,
.ant-table-wrapper .ant-table-thead > tr > td {
  font-size: 14px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: rgba(10, 33, 46, 1);
  background-color: rgba(239, 240, 245, 1);
}

/deep/.ant-table-wrapper .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr {
  color: rgba(104, 118, 126, 1);
}

/deep/ .ant-table-wrapper .ant-table-thead > tr > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before,
.ant-table-wrapper .ant-table-thead > tr > td:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
  width: 0px;
}

a {
  color: #1E8EF2;
}
</style>
